import { CommonModule } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Inject,
	inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_SNACK_BAR_DATA, MatSnackBarRef } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ComponentStore } from '@ngrx/component-store';
import { MessageDto } from '@siza-staff-care/shared/data';
import { StaffCareDateTimePipe } from '@siza-staff-care/shared/pipes';
import { Observable, interval, map, tap, withLatestFrom } from 'rxjs';
import { NotificationSeconds } from '../unified-poller.store';
import { NgEventBus } from 'ng-event-bus';
import { EventBusEvents } from '@siza-staff-care/event-bus-constants';
import { AuthStore } from '../auth.store';

interface State {
	message: MessageDto;
	showTime: Date;
	secondsLeft: number;
}

@Component({
	selector: 'sc-new-message-custom-snackbar',
	standalone: true,
	imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule, StaffCareDateTimePipe],
	template: `
		@if (message$ | async; as message) {
			<div class="header">
				<span class="subject">{{ message.subject }}</span>
				<div class="from">
					<span class="from-label">From</span>
					<span class="from-content">{{ message.from }}</span>
				</div>
			</div>

			<div class="body-wrapper" (click)="openMessages()">
				<div class="body" [innerHTML]="bodyHtml"></div>
				<div class="bottom-fade" [hidden]="!showBottomFade"></div>
			</div>

			<div class="footer">
				<span class="timestamp">{{ message.tsCreated | scDateTime }}</span>

				<div class="flex"></div>

				<div class="spinner-wrapper">
					<mat-progress-spinner diameter="28" color="warn" [value]="percentageTime$ | async" />
					<span class="seconds-left">{{ secondsLeft$ | async }}</span>
				</div>

				<button (click)="markAsRead()" color="primary" mat-button>Mark as read</button>
			</div>
		}
	`,
	styles: [
		`
			@import 'theme-variables';

			:host {
				display: block;
				width: clamp(250px, 90vw, 550px);

				.header {
					display: flex;
					align-items: center;

					.subject {
						font-size: 1rem;
						overflow: hidden;
						text-overflow: ellipsis;
						white-space: nowrap;
					}

					span {
						display: inline-block;
					}

					.subject {
						font-weight: 500;
						flex: 1;
						padding-right: 12px;
					}

					.from-label {
						text-transform: uppercase;
						font-size: 0.7em;
						font-weight: 500;
						color: #ddd;
						padding-right: 5px;
					}
				}

				.body-wrapper {
					position: relative;
					overflow: hidden;
					max-height: 100px;

					.body {
						white-space: pre-wrap;
						min-height: 30px;
					}

					.bottom-fade {
						height: 50px;
						position: absolute;
						width: 100%;
						bottom: 0;
						pointer-events: none;
						background: linear-gradient(to bottom, transparent, var(--mdc-snackbar-container-color));
					}
				}

				.footer {
					display: flex;
					align-items: center;

					.timestamp {
						font-size: 0.9em;
						color: #ddddddcc;
					}

					.flex {
						flex: 1;
					}

					.spinner-wrapper {
						position: relative;
						.seconds-left {
							position: absolute;
							inset: 0;
							z-index: 1;
							line-height: 30px; // Same height as spinner diameter
							text-align: center;
							opacity: 0.8;
							font-size: 12px;
							padding-right: 2px;
						}
					}

					button {
						color: #ffffffee;
						background-color: #666;
						margin-left: 12px;
					}
				}
			}
		`,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewMessageCustomSnackbarComponent extends ComponentStore<State> implements AfterViewInit {
	private authStore = inject(AuthStore);

	bodyHtml?: SafeHtml;
	measuredHeight = 9999;
	showBottomFade = false;

	readonly message$ = this.select(({ message }) => message);
	readonly showTime$ = this.select(({ showTime }) => showTime);
	readonly percentageTime$ = interval(300).pipe(
		withLatestFrom(this.showTime$),
		map(([, showTime]) => {
			const now = new Date();
			const diff = now.getTime() - showTime.getTime();
			const percentage = diff / (NotificationSeconds * 1000);

			this.patchState({ secondsLeft: Math.max(0, Math.round(NotificationSeconds - diff / 1000)) });

			return Math.max(5, percentage * 100);
		}),
	);
	readonly secondsLeft$ = this.select(({ secondsLeft }) => secondsLeft);

	readonly markAsRead = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.message$),
			tap(([, message]) => {
				this.eventBus.cast(EventBusEvents.Event_SnackBar_MarkMessageRead, message);
				this.snackBarRef.dismiss();
			}),
		),
	);

	readonly openMessages = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.eventBus.cast(EventBusEvents.Event_SnackBar_OpenMessages);
				this.snackBarRef.dismiss();
			}),
		),
	);

	private readonly closeOnLogout = this.effect((loggedOut$: Observable<void>) =>
		loggedOut$.pipe(
			tap(() => {
				this.snackBarRef.dismiss();
			}),
		),
	);

	constructor(
		protected snackBarRef: MatSnackBarRef<NewMessageCustomSnackbarComponent>,
		@Inject(MAT_SNACK_BAR_DATA) public message: MessageDto,
		private domSanitizer: DomSanitizer,
		private el: ElementRef,
		private eventBus: NgEventBus,
		private cdRef: ChangeDetectorRef,
	) {
		super({
			message,
			showTime: new Date(),
			secondsLeft: NotificationSeconds,
		});

		this.bodyHtml = this.domSanitizer.bypassSecurityTrustHtml(message.body);
		this.closeOnLogout(this.authStore.loggedOut$);
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			const bodyWrapper = this.el.nativeElement.querySelectorAll('.body-wrapper');

			const h = bodyWrapper[0].offsetHeight;
			this.measuredHeight = bodyWrapper[0].scrollHeight;
			this.showBottomFade = this.measuredHeight > h;

			this.cdRef.markForCheck();
		});
	}
}
